'use client';

import React, { useState } from 'react';
import { Box, Typography, Stepper, Step, StepLabel, StepContent, Paper, Button } from '@mui/material';
import { CalendarStepInstruction } from './types';

interface TripleSeatInstructionsProps {
    onComplete?: () => void;
    showCompleteButton?: boolean;
    variant?: 'modal' | 'inline';
}

const TripleSeatInstructions: React.FC<TripleSeatInstructionsProps> = ({
    onComplete,
    showCompleteButton = true,
    variant = 'modal'
}) => {
    const [activeStep, setActiveStep] = useState(0);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleComplete = () => {
        onComplete?.();
    };

    const steps: CalendarStepInstruction[] = [
        {
            label: 'Export from Tripleseat',
            description: (
                <>
                    <Typography variant="body2" paragraph className="text-gray-300">
                        Go to your Tripleseat calendar page and click the "Export" button at the bottom right.
                    </Typography>
                    <img
                        src="/img/tripleseat1.png"
                        alt="Tripleseat Export Button"
                        className="max-w-full rounded-lg border border-gray-800 mb-4"
                    />
                </>
            ),
        },
        {
            label: 'Copy calendar link',
            description: (
                <>
                    <Typography variant="body2" paragraph className="text-gray-300">
                        On the export page, copy the calendar link for your venue (not individual sections).
                    </Typography>
                    <img
                        src="/img/tripleseat2.png"
                        alt="Tripleseat Calendar Link"
                        className="max-w-full rounded-lg border border-gray-800 mb-4"
                    />
                </>
            ),
        },
        {
            label: 'Add to Google Calendar',
            description: (
                <>
                    <Typography variant="body2" paragraph className="text-gray-300">
                        In Google Calendar, click the settings icon in the top right corner.
                    </Typography>
                    <Typography variant="body2" paragraph className="text-gray-300">
                        Select "Add calendar from URL" and paste the Tripleseat calendar link.
                    </Typography>
                    <img
                        src="/img/tripleseat3.png"
                        alt="Google Calendar Add URL"
                        className="max-w-full rounded-lg border border-gray-800 mb-4"
                    />
                    <img
                        src="/img/tripleseat4.png"
                        alt="Google Calendar Add URL"
                        className="max-w-full rounded-lg border border-gray-800 mb-4"
                    />
                </>
            ),
        },
    ];

    const containerStyles = variant === 'modal'
        ? "max-w-2xl mx-auto bg-gray-900 rounded-xl p-6 border border-[#E0B1F1]/20"
        : "w-full bg-gray-900/50 rounded-xl p-6 border border-[#E0B1F1]/20";

    return (
        <Box className={containerStyles}>
            <Typography variant="body1" sx={{ mb: 3 }} className="text-gray-300">
                Follow these steps to connect your Tripleseat calendar with Litty:
            </Typography>

            <Stepper activeStep={activeStep} orientation="vertical" sx={{
                '& .MuiStepLabel-label': {
                    color: 'white',
                    fontWeight: 'medium'
                },
                '& .MuiStepLabel-root .Mui-active': {
                    color: '#C0C0DD',
                },
                '& .MuiStepLabel-root .Mui-completed': {
                    color: '#C0C0DD',
                },
                '& .MuiStepConnector-line': {
                    borderColor: 'rgba(192, 192, 221, 0.3)',
                }
            }}>
                {steps.map((step, index) => (
                    <Step key={step.label}>
                        <StepLabel>{step.label}</StepLabel>
                        <StepContent>
                            <Box sx={{ mb: 2 }}>
                                {step.description}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        disabled={index === 0}
                                        onClick={handleBack}
                                        sx={{
                                            color: '#C0C0DD',
                                            borderColor: '#C0C0DD',
                                            '&:hover': {
                                                borderColor: '#E0B1F1',
                                                backgroundColor: 'rgba(224, 177, 241, 0.1)'
                                            },
                                            '&.Mui-disabled': {
                                                color: 'rgba(255, 255, 255, 0.3)',
                                                borderColor: 'rgba(255, 255, 255, 0.1)'
                                            }
                                        }}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={index === steps.length - 1 && showCompleteButton ? handleComplete : handleNext}
                                        sx={{
                                            bgcolor: '#C0C0DD',
                                            color: '#1C1C34',
                                            fontWeight: 600,
                                            '&:hover': {
                                                bgcolor: '#E0B1F1',
                                            },
                                        }}
                                    >
                                        {index === steps.length - 1 && showCompleteButton ? 'Connect Google Calendar To Litty' : 'Continue'}
                                    </Button>
                                </Box>
                            </Box>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>

            {activeStep === steps.length && (
                <Paper square elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.05)', color: 'white', border: '1px solid rgba(224, 177, 241, 0.2)', borderRadius: '10px' }}>
                    <Typography className="text-gray-200">
                        All steps completed - you&apos;re ready to connect your Google Calendar to Litty
                    </Typography>
                    {showCompleteButton && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={handleComplete}
                                sx={{
                                    bgcolor: '#C0C0DD',
                                    color: '#1C1C34',
                                    fontWeight: 600,
                                    '&:hover': {
                                        bgcolor: '#E0B1F1',
                                    },
                                }}
                            >
                                Connect Google Calendar To Litty
                            </Button>
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default TripleSeatInstructions; 